import { useMemo } from 'react';
import { DataGrid } from '@/components/tables/DataGrid';
import type { DataGridColumn } from '@/components/tables/DataGrid';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { providersApi } from '@/api/services/providers';
import type {
  ProviderAccountFeeRowDto,
  ProviderAccountLogRowDto,
} from '@/api/types/providers';
import { formatDateTime as fmtDateTime } from '@/utils/format';

function formatDateTime(iso: string): string {
  return fmtDateTime(iso);
}

function num(v: number | null | undefined, digits = 2): string {
  if (v === null || v === undefined) return '';
  return v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

interface Props {
  providerAccountId: string;
}

// Mirrors Partials/_UpdateLogs.cshtml and Partials/_FeesLogs.cshtml, shown only
// in edit mode.
export function AccountLogsGrids({ providerAccountId }: Props) {
  const extraParams = useMemo(() => ({ providerAccountId }), [providerAccountId]);

  const logsTable = useServerDataTable<ProviderAccountLogRowDto, { providerAccountId: string }>({
    queryKey: ['providers', 'account-logs', providerAccountId],
    fetcher: (req) => providersApi.getAccountLogsForDataTable(req),
    extraParams,
  });

  const feesTable = useServerDataTable<ProviderAccountFeeRowDto, { providerAccountId: string }>({
    queryKey: ['providers', 'account-fees', providerAccountId],
    fetcher: (req) => providersApi.getAccountFeesForDataTable(req),
    extraParams,
  });

  const logColumns: DataGridColumn<ProviderAccountLogRowDto>[] = [
    { key: 'userName', header: 'Updated By', cell: (r) => r.userName },
    { key: 'creationTime', header: 'Updated At', sortable: true, cell: (r) => formatDateTime(r.creationTime) },
    { key: 'oldHoldingReserves', header: 'Old Holding Reserves', sortable: true, cell: (r) => num(r.oldHoldingReserves) },
    { key: 'newHoldingReserves', header: 'New Holding Reserves', sortable: true, cell: (r) => num(r.newHoldingReserves) },
    { key: 'oldRollingDays', header: 'Old Rolling Days', sortable: true, cell: (r) => num(r.oldRollingDays, 0) },
    { key: 'newRollingDays', header: 'New Rolling Days', sortable: true, cell: (r) => num(r.newRollingDays, 0) },
    { key: 'oldOwnership', header: 'Old Ownership', sortable: true, cell: (r) => r.oldOwnership },
    { key: 'newOwnership', header: 'New Ownership', sortable: true, cell: (r) => r.newOwnership },
    { key: 'oldProcessSwitch', header: 'Old Process D-1', sortable: true, cell: (r) => r.oldProcessSwitch },
    { key: 'newProcessSwitch', header: 'New Process D-1', sortable: true, cell: (r) => r.newProcessSwitch },
    { key: 'oldTransactionPlusXDays', header: 'Old Transaction + X Days', sortable: true, cell: (r) => num(r.oldTransactionPlusXDays, 0) },
    { key: 'newTransactionPlusXDays', header: 'New Transaction + X Days', sortable: true, cell: (r) => num(r.newTransactionPlusXDays, 0) },
    { key: 'oldConnectedClientBankAccount', header: 'Old Connected Client Bank Account', cell: (r) => r.oldConnectedClientBankAccount },
    { key: 'newConnectedClientBankAccount', header: 'New Connected Client Bank Account', cell: (r) => r.newConnectedClientBankAccount },
    { key: 'oldIncludeStatementsInProviderAssets', header: 'Old Include Statements In Provider Assets', sortable: true, cell: (r) => r.oldIncludeStatementsInProviderAssets },
    { key: 'newIncludeStatementsInProviderAssets', header: 'New Include Statements In Provider Assets', sortable: true, cell: (r) => r.newIncludeStatementsInProviderAssets },
  ];

  const feeColumns: DataGridColumn<ProviderAccountFeeRowDto>[] = [
    { key: 'creationTime', header: 'Updated At', sortable: true, cell: (r) => formatDateTime(r.creationTime) },
    { key: 'depositFixedFee', header: 'Deposit Fixed Fee', sortable: true, cell: (r) => num(r.depositFixedFee) },
    { key: 'withdrawalFixedFee', header: 'Withdrawal Fixed Fee', sortable: true, cell: (r) => num(r.withdrawalFixedFee) },
    { key: 'depositPercentageFee', header: 'Percentage Fee On Deposit', sortable: true, cell: (r) => num(r.depositPercentageFee) },
    { key: 'withdrawalPercentageFee', header: 'Percentage Fee On Withdrawal', sortable: true, cell: (r) => num(r.withdrawalPercentageFee) },
    { key: 'threshold', header: 'Threshold', sortable: true, cell: (r) => num(r.threshold) },
    { key: 'thresholdMaxIfConditionBetween', header: 'Threshold If Condition Between', sortable: true, cell: (r) => num(r.thresholdMaxIfConditionBetween) },
    { key: 'conditionType', header: 'Condition Type', sortable: true, cell: (r) => r.conditionType },
  ];

  return (
    <>
      <div className="card custom-card">
        <div className="card-header border-bottom">
          <h3 className="card-title">Update Logs</h3>
        </div>
        <div className="card-body">
          <DataGrid
            controller={logsTable}
            columns={logColumns}
            rowKey={(r) => `${r.creationTime}-${r.userName}`}
            emptyMessage="No update logs."
          />
        </div>
      </div>

      <div className="card custom-card">
        <div className="card-header border-bottom">
          <h3 className="card-title">Update Fees Logs</h3>
        </div>
        <div className="card-body">
          <DataGrid
            controller={feesTable}
            columns={feeColumns}
            rowKey={(r) => `${r.creationTime}-${r.conditionType}`}
            emptyMessage="No fee logs."
          />
        </div>
      </div>
    </>
  );
}
